import { ReactElement, useMemo, useState } from 'react';
import { StatusBar, PrimaryButton, SnackbarStatus, Typography, SecondaryButton } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import ModalWrapper from '../ModalWrapper';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import SelectableListWithControls from '../SelectableListWithControls/SelectableListWithControls';
import { ILinkVenuesModalProps } from './';
import { updateMenuVenues } from 'src/api/updateMenuVenues/updateMenuVenues';
import { IUpdateMenuVenuesRequest } from 'src/api/updateMenuVenues/types';
import ModalImg from '../../assets/images/chef-plating.png';
import styles from './LinkVenuesModal.module.scss';
import { useMenu } from 'src/hooks/useMenu/useMenu';
import { useVenues } from 'src/hooks/useVenues/useVenues';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';

const LinkVenuesFlow = ({ closeModal, menu, status, menuId }: Omit<ILinkVenuesModalProps, 'open'>): ReactElement => {
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { trackEvent } = useUserTracking();
  const { data: venues, isLoading } = useVenues();
  const { refetch } = useMenu(menuId);

  const [selectedVenueIds, setSelectedVenueIds] = useState<string[]>(() => Array.from(menu.linkedVenueIds));
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const venueOptions = useMemo(
    () => venues?.map((venue) => ({ id: venue.venue_id, label: venue.name })) ?? [],
    [venues],
  );

  const selectedVenueOptions = useMemo(
    () => venueOptions.filter(({ id }) => selectedVenueIds.includes(id)),
    [venueOptions, selectedVenueIds],
  );

  const onSave = async () => {
    clearError();
    setIsSubmitting(true);

    const requestBody: IUpdateMenuVenuesRequest = {
      venue_ids: selectedVenueIds,
    };

    try {
      await updateMenuVenues(menuId, requestBody);
      trackEvent('menu_venues_linked', { menuId, venueCount: selectedVenueIds.length });
      refetch();
      closeModal();
    } catch {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: 'We have been unable to complete your request. Please try again',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      name="linked-venues"
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      <main>
        <Typography weight="semi-bold" size="xxlarge" className={styles.heading}>
          Link Venues
        </Typography>
        <SelectableListWithControls
          options={venueOptions}
          selectedOptionsIds={selectedVenueIds}
          onOptionChange={setSelectedVenueIds}
          collapsible={{ selectedOptionsData: selectedVenueOptions }}
          listDetails={{
            title: 'Venues:',
            emptyMessage: 'Add your first venue',
            isLoading: isLoading || status === 'loading',
          }}
        />
        <ButtonContainer
          left={
            <SecondaryButton size="full-width" buttonStyle="outline" onClick={closeModal}>
              Cancel
            </SecondaryButton>
          }
          right={
            <PrimaryButton size="full-width" type="submit" loading={isSubmitting || isLoading} disabled={isSubmitting}>
              Save
            </PrimaryButton>
          }
        />
        <div className={styles['error-banner']}>
          {errorMessage && <StatusBar message={errorMessage} variant="error" />}
        </div>
      </main>
    </form>
  );
};

const LinkVenuesModal = ({ closeModal, menu, open, status, menuId }: ILinkVenuesModalProps): ReactElement => {
  return (
    <ModalWrapper closeModal={closeModal} open={open} image={ModalImg} imageAlt="Chef preparing dishes">
      <LinkVenuesFlow closeModal={closeModal} menuId={menuId} status={status} menu={menu} />
    </ModalWrapper>
  );
};

export default LinkVenuesModal;
